/**
 * Whether selecting a numbered/gallery background must also drop the shared
 * custom-bg upload (customBgs.main).
 *
 * Ads is the one channel whose templates render customBgs.main with priority
 * over the baked per-bgOption asset, independent of bgOption — so leaving it
 * in place makes numbered clicks visually dead and the upload unremovable
 * (the shared upload's only UI is BgSelector's upload button, which has no
 * remove). Livestream must keep its .main: templates there render
 * customBgs[id], and .main is the shared copy the unlock revert restores
 * (see TemplateControls/unlockBgRevert). Gallery channels ignore .main.
 *
 * Per-template uploads (customBgs[id] on locked templates) are untouched —
 * a detached template keeps its own background by design.
 */
export function shouldClearSharedUploadOnBgSelect({
  channelValue,
  customBgs,
}: {
  channelValue: string | undefined;
  customBgs: Record<string, string | undefined> | undefined;
}): boolean {
  return channelValue === "ads" && !!customBgs?.main;
}

/**
 * Whether a banner's own image follows the background number on this channel.
 * `background=N` is the image each banner has for N (its default-scene
 * preset), so picking or uploading a background in the sidebar gives every
 * banner of the page the same image again. Livestream keeps its per-product
 * artworks in `customBgs` whatever the background, so it is left out.
 */
export function bannerImagesFollowBackground(channelValue: string | undefined): boolean {
  return channelValue !== "livestream";
}

/**
 * The `customBgs` record after a background pick in the sidebar: the banner
 * images go — detached banners' uploads too. Another background loads its
 * presets with its default scene; a pick of the background on screen loads
 * nothing, so its presets (`bgPresets`, the snapshot its default left) come
 * back here. The shared slot (`main`) stays.
 */
export function customBgsAfterBgSelect(
  customBgs: Record<string, string | undefined> | undefined,
  samePresets?: Record<string, {customBg?: string} | undefined> | null
): Record<string, string | undefined> {
  const next: Record<string, string | undefined> =
    customBgs?.main === undefined ? {} : {main: customBgs.main};
  for (const [id, preset] of Object.entries(samePresets ?? {})) {
    if (id !== "main" && preset?.customBg) next[id] = preset.customBg;
  }
  return next;
}
